import { Link } from 'react-router-dom';
import { Globe, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, ArrowRight } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = {
    company: {
      title: 'Company',
      links: [
        { name: 'About Osaka Group', path: '/about/osaka-group' },
        { name: 'Legacy Since 1993', path: '/about/legacy' },
        { name: 'Leadership Team', path: '/about/leadership' },
        { name: 'Awards & Recognition', path: '/about/awards' },
        { name: 'Careers', path: '/corporate/careers' },
        { name: 'Investor Relations', path: '/corporate/investors' },
      ]
    },
    platform: {
      title: 'Platform',
      links: [
        { name: 'Travel Hypermarket', path: '/platform/hypermarket' },
        { name: 'Why OsakaConnect', path: '/platform/why-osaka' },
        { name: 'AI Intelligence', path: '/ai-tools' },
        { name: 'Platform Demo', path: '/demo' },
        { name: 'Security', path: '/platform/security' },
      ]
    },
    products: {
      title: 'Products',
      links: [
        { name: 'Flights', path: '/products/flights' },
        { name: 'Hotels', path: '/products/hotels' },
        { name: 'Visa Services', path: '/products/visa' },
        { name: 'Travel Insurance', path: '/products/insurance' },
        { name: 'View All 24 Products', path: '/products' },
      ]
    },
    partners: {
      title: 'Partners',
      links: [
        { name: 'B2B Travel Agents', path: '/partners/b2b' },
        { name: 'B2E Corporate', path: '/partners/b2e' },
        { name: 'API Partners', path: '/partners/api' },
        { name: 'White Label', path: '/partners/whitelabel' },
        { name: 'Become a Partner', path: '/signup' },
      ]
    },
    developers: {
      title: 'Developers',
      links: [
        { name: 'Developer Portal', path: '/developers' },
        { name: 'API Documentation', path: '/developers/docs' },
        { name: 'API Sandbox', path: '/developers/sandbox' },
        { name: 'API Pricing', path: '/developers/pricing' },
      ]
    },
    resources: {
      title: 'Resources',
      links: [
        { name: 'Help Center', path: '/support/help' },
        { name: 'Knowledge Base', path: '/support/knowledge' },
        { name: 'Training Academy', path: '/support/training' },
        { name: 'Blog & Insights', path: '/resources/blog' },
        { name: 'Download Center', path: '/resources/downloads' },
      ]
    },
  };

  const legalLinks = [
    { name: 'Privacy Policy', path: '/legal/privacy' },
    { name: 'Terms of Service', path: '/legal/terms' },
    { name: 'Security Policy', path: '/legal/security' },
    { name: 'GDPR Compliance', path: '/legal/gdpr' },
    { name: 'Cookie Policy', path: '/legal/cookies' },
  ];

  const trustBadges = ['IATA Certified', 'RBI Authorized', 'ISO Certified', 'SSL Secured', 'GDPR Compliant'];

  return (
    <footer className="bg-[#0F172A] text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key}>
              <h4 className="font-outfit font-semibold text-sm mb-4 text-white">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className="text-slate-400 hover:text-[#00C2CB] transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Brand & Contact Row */}
        <div className="mt-16 pt-8 border-t border-slate-800">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img 
                  src="/osaka-logo.svg" 
                  alt="OsakaConnect - Travel Hypermarket" 
                  className="h-12 w-auto brightness-0 invert"
                />
              </Link>
              <p className="text-slate-400 text-sm leading-relaxed mb-4 max-w-md">
                World's First Travel Hypermarket & AI-Powered Super App. One unified platform with 24+ travel products serving 120+ countries since 1993.
              </p>
              <div className="flex items-center gap-3">
                <a href="#" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-[#00C2CB] flex items-center justify-center transition-colors" aria-label="LinkedIn">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href="#" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-[#00C2CB] flex items-center justify-center transition-colors" aria-label="Twitter">
                  <Twitter className="w-4 h-4" />
                </a>
                <a href="#" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-[#00C2CB] flex items-center justify-center transition-colors" aria-label="Facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="#" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-[#00C2CB] flex items-center justify-center transition-colors" aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-outfit font-semibold text-sm mb-4 text-white">Headquarters</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-[#00C2CB] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-400 text-sm">Angamaly, Kerala, India</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#00C2CB] flex-shrink-0" />
                  <a href="mailto:info@osakaconnect.com" className="text-slate-400 hover:text-[#00C2CB] text-sm">
                    info@osakaconnect.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#00C2CB] flex-shrink-0" />
                  <a href="tel:+919778620822" className="text-slate-400 hover:text-[#00C2CB] text-sm">+91 97786 20822 / 85899 78777</a>
                </li>
              </ul>
            </div>

            {/* Global Offices */}
            <div>
              <h4 className="font-outfit font-semibold text-sm mb-4 text-white">Global Offices</h4>
              <p className="text-slate-400 text-sm mb-3">9 International + 18 India Offices</p>
              <Link 
                to="/global-presence"
                className="text-[#00C2CB] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
              >
                View All Locations <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 pt-8 border-t border-slate-800">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {trustBadges.map((badge) => (
              <div 
                key={badge}
                className="px-4 py-2 rounded-full bg-slate-800/50 text-xs font-medium text-slate-300 border border-slate-700"
              >
                {badge}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {currentYear} OsakaConnect. Osaka Group of Companies | Est. 1993. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {legalLinks.map((link) => (
                <Link 
                  key={link.path}
                  to={link.path}
                  className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
